import type Quill from 'quill';
import type {Delta, EmitterSource} from 'quill';

import {isNil} from 'ramda';

import {RichTextTemplateTokens} from '../types';
import {buildTypedTemplateTokenConversion} from './buildTypedTemplateTokenConversion';
import {createTemplateTokenClipboardMatchers} from './createTemplateTokenClipboardMatchers';
import {serializeTemplateTokens} from './serializeTemplateTokens';
import {writeTemplateTokenClipboard} from './writeTemplateTokenClipboard';

/**
 * The token behaviour of one editor. `getLabel` is read at each conversion, so it always sees the
 * current label catalogue.
 */
export function createTemplateTokenSession(getLabel: RichTextTemplateTokens['getLabel']) {
  let lastHtml: string | null = null;
  let lastCanonical = '';
  let editorCanonical: string | null = null;

  const toCanonical = (html: string) => {
    if (html !== lastHtml) {
      lastHtml = html;
      lastCanonical = serializeTemplateTokens(html);
    }

    return lastCanonical;
  };

  const attach = (editor: Quill) => {
    editorCanonical = toCanonical(editor.root.innerHTML);

    const handleTextChange = (change: Delta, _oldContents: Delta, source: EmitterSource) => {
      editorCanonical = toCanonical(editor.root.innerHTML);

      if (source !== 'user' || editor.history.ignoreChange) {
        return;
      }

      const conversion = buildTypedTemplateTokenConversion(change, editor.getContents(), getLabel);

      if (isNil(conversion)) {
        return;
      }

      // A user change does not move the selection: keep the caret where the typed text ended.
      const range = editor.getSelection();

      editor.history.cutoff();
      editor.updateContents(conversion, 'user');
      editor.history.cutoff();
      if (!isNil(range)) {
        editor.setSelection(conversion.transformPosition(range.index), 'silent');
      }
    };
    const handleClipboard = (event: ClipboardEvent) => writeTemplateTokenClipboard(editor, event);

    editor.on('text-change', handleTextChange);
    editor.root.addEventListener('copy', handleClipboard, true);
    editor.root.addEventListener('cut', handleClipboard, true);

    return () => {
      editor.off('text-change', handleTextChange);
      editor.root.removeEventListener('copy', handleClipboard, true);
      editor.root.removeEventListener('cut', handleClipboard, true);
      editorCanonical = null;
    };
  };

  return {
    clipboardMatchers: createTemplateTokenClipboardMatchers(getLabel),
    attach,
    toCanonical,
    /**
     * The canonical value of a user change, or null when the change left it as it was (a token
     * conversion of text that already held the same placeholder). Call it from ReactQuill's
     * `onChange`: Quill emits `editor-change`, which ReactQuill listens to, before `text-change`,
     * which updates the value compared here, so it still holds the value before the change.
     */
    readUserChange: (html: string) => {
      const canonical = toCanonical(html);

      return canonical === editorCanonical ? null : canonical;
    },
  };
}
